import { ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

export function SecurityPage() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-8">
          <ArrowLeft size={16} /> Back
        </button>

        {/* Hero */}
        <div className="mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Security Overview</h1>
          <p className="text-gray-600 text-lg leading-relaxed">
            At Dr. Scribey, safeguarding clinical and patient data is our top priority. Our security program combines best-in-class technology, rigorous processes, and HIPAA-compliant infrastructure to ensure confidentiality, integrity, and availability of your data.
          </p>
        </div>

        <div className="space-y-10">

          {/* 1 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">1. PHI Minimization</h2>
            <p className="text-gray-600 leading-relaxed">
              Before a clinical transcript reaches our AI drafting models, patient identifiers — names, dates of birth, medical record numbers, phone numbers, and addresses — are replaced with anonymous tokens. Real identifiers are restored only after processing, within your private encrypted session. All AI processing runs on HIPAA-eligible infrastructure under Business Associate Agreements, so PHI is contractually protected at every layer — tokenization is an additional safeguard on top, not the only line of defense.
            </p>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Data Protection</h2>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>Encryption at rest:</strong> All databases and storage are encrypted using AES-256 encryption with managed keys, validated to FIPS 140-2 standards.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>Encryption in transit:</strong> Every connection — client-to-server and service-to-service — is encrypted via TLS 1.2 or higher.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>Zero audio retention:</strong> Audio is streamed in real-time for transcription and is <strong>permanently discarded</strong> once processing is complete. No audio recordings are ever stored — not on our servers, not by our transcription providers, not on your device.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>Zero image retention:</strong> Photos, screenshots, and camera captures used for schedule import or patient scanning are processed entirely in-memory and <strong>permanently discarded</strong> immediately after extraction. No images are ever stored on our servers, written to disk, saved in databases, or included in log files. Only the extracted text data (patient names, appointment times) is retained.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>No client-side caching:</strong> Clinical data is never cached in the browser. API responses include no-store directives to prevent local persistence.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>Configurable data retention:</strong> Delete notes and transcripts manually at any time, or configure automatic deletion after 30, 60, or 90 days.</span>
              </li>
            </ul>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Access Control</h2>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>Provider isolation:</strong> Each provider's data is fully isolated. You can only access your own patients, notes, and recordings.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>Automatic session timeout:</strong> Sessions lock automatically after 30 minutes of inactivity, as required by HIPAA.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>Multi-factor authentication:</strong> MFA and Single Sign-On (SSO) available for additional security layers.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>Least-privilege access:</strong> Internal systems follow least-privilege principles — each service has only the permissions necessary to perform its function.</span>
              </li>
            </ul>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">4. AI Safety</h2>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>Automated review:</strong> Every generated note passes through an independent AI safety layer that checks for hallucinations, omissions, and clinical accuracy before you see it.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>No fabrication:</strong> Notes can contain less than what you dictated — never more. The AI does not invent clinical information.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>Provenance tracking:</strong> Every sentence in your note traces back to the specific segment of your recording it came from.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>No training on your data:</strong> Your clinical data is never used to train AI models — not ours, not anyone else's. This is contractually guaranteed by all service providers.</span>
              </li>
            </ul>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Audit Trail</h2>
            <p className="text-gray-600 leading-relaxed">
              Every note generation, edit, revision, and approval is logged with timestamps and user identity. Audit logs are immutable — they cannot be modified or deleted. This provides a complete chain of custody for every clinical document, supporting compliance and quality assurance.
            </p>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Compliance and Certifications</h2>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>HIPAA and HITECH:</strong> Dr. Scribey meets or exceeds all requirements for protecting patient health information. We sign a Business Associate Agreement (BAA) with every customer.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>FIPS 140-2:</strong> Cryptographic modules used for data encryption follow Federal Information Processing Standards (FIPS PUB 140-2).</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>SOC 2 Type II:</strong> Independent third-party security certification on our roadmap.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>Subprocessors:</strong> All infrastructure and service providers maintain BAAs and are contractually prohibited from retaining or using your data beyond the scope of their services.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>US-based infrastructure:</strong> All data is processed and stored exclusively within the United States.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>Data ownership:</strong> You own everything you create. Export or delete your data at any time — even after your subscription ends.</span>
              </li>
            </ul>
          </section>

          {/* 7 - Patient Consent */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Patient Consent Best Practices</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              We encourage transparent communication with patients about AI-assisted documentation. While consent requirements vary by jurisdiction, here are common approaches used by our providers:
            </p>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>Verbal confirmation:</strong> A brief statement at the start of the visit — <em>"I use an AI tool to help with my clinical notes. It listens to our conversation to generate documentation. Is that okay with you?"</em></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>Waiting room signage:</strong> A posted notice informing patients that visits may use ambient AI documentation technology.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5 font-bold">·</span>
                <span><strong>Intake forms:</strong> A consent acknowledgment included in digital or paper intake forms.</span>
              </li>
            </ul>
            <p className="text-gray-500 text-sm mt-3">
              Consent practices are your responsibility as the treating provider. Building consent into your workflow promotes patient trust and supports regulatory compliance.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-indigo-50 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Questions?</h2>
            <p className="text-gray-600">
              For security inquiries or to request our BAA, contact us at{' '}
              <a href="mailto:info@tailwindx.org" className="text-indigo-600 hover:text-indigo-700 font-medium">
                info@tailwindx.org
              </a>
            </p>
          </section>

          {/* Legal links */}
          <div className="flex flex-wrap gap-4 text-sm text-gray-500 pt-4 border-t border-gray-200">
            <a href="/terms" className="hover:text-indigo-600">Terms of Service</a>
            <a href="/privacy" className="hover:text-indigo-600">Privacy Policy</a>
            <a href="/baa" className="hover:text-indigo-600">Business Associate Agreement</a>
          </div>
        </div>

        <footer className="mt-16 pt-8 border-t border-gray-200 text-center text-sm text-gray-400">
          © {new Date().getFullYear()} TailwindX, LLC. All rights reserved.
        </footer>
      </div>
    </div>
  )
}
